// =============================================================================
// Rodzaje i statusy zadań agenta Receptora — moduł BEZ zależności od Supabase.
//
// Osobny plik nie jest kaprysem: `receptorCoreApi.ts` importuje klienta
// Supabase, który przy wczytaniu wymaga zmiennych środowiskowych, a bramka
// `tests/unit/apps/rodzajeZadanReceptora.test.ts` zestawia listę rodzajów
// PANELU z listą USŁUGI i musi dać się uruchomić z korzenia repozytorium
// bez konfiguracji powłoki. Wartości nie da się zaimportować z usługi
// (powłoka jest aplikacją Vite, `services/*` nie są jej zależnością), więc
// lista żyje w kopii — a ta bramka sprawia, że kopia nie rozjedzie się po
// cichu z `RODZAJE_ZADAN` w `services/receptor-core/src/zadania.ts`.
//
// Od okna agenta 0.6.0 (plan okna, D8) tą kopią jest SŁOWNIK WSPÓLNY
// `@insolitum/types/receptor`, bo te same etykiety i zdania czyta też okno
// agenta. Panel wystawia jego obiekty pod dotychczasowymi nazwami
// (`RODZAJE_ZADAN_PANELU`, `OPIS_STANU_PRODUKCJI`, `OPIS_POWODU_ZDOLNOSCI`,
// stałe `RODZAJ_*`) — to jeden obiekt pod dwiema nazwami importu, nie kopia,
// i bramka sprawdza to tożsamością (`===`). Etykiet rodzajów i zdań słownika
// nie wpisuje się tu drugi raz.
// =============================================================================

import {
  OPIS_POWODU_ZDOLNOSCI,
  OPIS_STANU_PRODUKCJI,
  RODZAJE_ZADAN_RECEPTORA,
  RODZAJ_ODCZYTU_DOKUMENTOW,
  RODZAJ_ODCZYTU_NASTAW,
  RODZAJ_ODCZYTU_UZYTKOWNIKOW,
  RODZAJ_POBRANIA_PINU,
  RODZAJ_POTWIERDZENIA_DOKUMENTOW,
  type OpisRodzajuZadania,
} from '@insolitum/types/receptor';

export type StatusZadania = 'oczekuje' | 'podjete' | 'zrobione' | 'blad';

export {
  OPIS_POWODU_ZDOLNOSCI,
  OPIS_STANU_PRODUKCJI,
  RODZAJ_ODCZYTU_DOKUMENTOW,
  RODZAJ_ODCZYTU_NASTAW,
  RODZAJ_ODCZYTU_UZYTKOWNIKOW,
  RODZAJ_POBRANIA_PINU,
  RODZAJ_POTWIERDZENIA_DOKUMENTOW,
  type OpisRodzajuZadania,
};

/** Źródło zadań zlecanych przez harmonogram usługi (kolumna `zrodlo_system`). */
export const ZRODLO_HARMONOGRAMU = 'harmonogram';

/**
 * Czy zadanie zlecił harmonogram. Harmonogram zakłada ich około 36 na agenta
 * na godzinę, więc lista „Ostatnie zadania” (10 pozycji) zalałaby się nimi
 * w kilkanaście minut, a każde `oczekuje`/`podjete` trzymałoby ekran na szybkim
 * odpytywaniu bez końca. Lista i odpytywanie ich NIE widzą — pierwszą linią
 * jest filtr zapytania w `listaZadan`, ta funkcja jest drugą (atrapa, cofnięty
 * filtr, odpowiedź z innej ścieżki).
 */
export function zadanieHarmonogramu(z: Pick<ZadanieAgenta, 'zrodlo_system'>): boolean {
  return z.zrodlo_system === ZRODLO_HARMONOGRAMU;
}

/**
 * WSZYSTKIE rodzaje zadań — klucze = kontrakt z usługą (`RODZAJE_ZADAN`).
 * Etykieta służy liście „Ostatnie zadania", `zPanelu` — przyciskom. TEN SAM
 * obiekt, co słownik wspólny (`RODZAJE_ZADAN_RECEPTORA`), nie jego kopia.
 */
export const RODZAJE_ZADAN_PANELU: Record<string, OpisRodzajuZadania> = RODZAJE_ZADAN_RECEPTORA;

/**
 * Statusy pobrania PIN-u (`wynik.status` zadania `receptor.pin.pobierz`) —
 * co do znaku te z kontraktu (migracja 758, receptor-core, agent 0.5.0).
 * `nieznany` dokłada usługa, gdy agent przyśle status spoza białej listy.
 */
export const STATUSY_PINU = [
  'przypisany',
  'zajety',
  'zly_format',
  'brak_pinu',
  'nieaktywny',
  'nie_znaleziono',
  'powiazanie_zmienione',
  'brak_pracownika',
  'nieznany',
] as const;

export type StatusPinu = (typeof STATUSY_PINU)[number];

/** Jedno zdanie na status — to samo na kafelku pracownika i w liście zadań agenta. */
export const OPIS_STATUSU_PINU: Record<StatusPinu, string> = {
  przypisany: 'PIN pobrany z Receptora',
  zajety: 'Ten PIN z Receptora ma już inny pracownik — wygeneruj nowy',
  zly_format: 'PIN w Receptorze nie ma 4 cyfr — wygeneruj nowy',
  brak_pinu:
    'Ta osoba nie ma PIN-u w Receptorze — nadaj go w Receptorze i pobierz ponownie albo wygeneruj nowy',
  nieaktywny: 'Konto tej osoby w Receptorze jest nieaktywne',
  nie_znaleziono: 'Tej osoby nie ma już w Receptorze',
  powiazanie_zmienione: 'Powiązanie zmieniło się w trakcie — pobierz ponownie',
  brak_pracownika: 'Pracownik nie istnieje',
  nieznany: 'Agent Receptora oddał nieznany wynik — pobierz ponownie albo wygeneruj nowy',
};

/**
 * Status z `wynik` zadania pobrania PIN-u. Czyta WYŁĄCZNIE pole `status` —
 * pola `pin` nie dotyka nigdy, nawet gdyby jakaś usterka je tam wpisała.
 * Status spoza listy to `nieznany`, a brak obiektu to `null`.
 */
export function statusPinuZWyniku(wynik: unknown): StatusPinu | null {
  if (typeof wynik !== 'object' || wynik === null) return null;
  const status = (wynik as { status?: unknown }).status;
  if (typeof status !== 'string') return null;
  return (STATUSY_PINU as readonly string[]).includes(status) ? (status as StatusPinu) : 'nieznany';
}

export interface ZadanieAgenta {
  id: string;
  organization_id: string;
  agent_id: string | null;
  rodzaj: string;
  parametry: Record<string, unknown>;
  status: StatusZadania;
  blad: string | null;
  proby: number;
  created_at: string;
  podjete_at: string | null;
  podjete_przez: string | null;
  zakonczone_at: string | null;
  /** System, który zlecił zadanie torem serwisowym (733); `null` = panel. */
  zrodlo_system: string | null;
  zrodlo_id: string | null;
  /** Wynik zadania — kształt zależy od rodzaju; panel pokazuje STRESZCZENIE. */
  wynik: unknown;
}

const NAZWY_SYSTEMOW: Record<string, string> = { mandatum: 'Mandatum' };

/**
 * Skąd przyszło zadanie — „Mandatum · ZP-202610-001" albo `null` dla zadania
 * z panelu. Nazwa zlecenia jest w `parametry.nazwa` (kontrakt z
 * `services/receptor-core/src/zlecenie.ts`).
 */
export function zrodloZadania(z: ZadanieAgenta): string | null {
  if (!z.zrodlo_system) return null;
  const system = NAZWY_SYSTEMOW[z.zrodlo_system] ?? z.zrodlo_system;
  const nazwa = typeof z.parametry?.nazwa === 'string' ? z.parametry.nazwa : null;
  return nazwa ? `${system} · ${nazwa}` : system;
}

/** Pozycja słownika z bazy Receptora (magazyn albo zmiana). */
export interface PozycjaSlownikaReceptora {
  id: number;
  symbol: string | null;
  nazwa: string | null;
}

export interface SlownikiNastaw {
  magazyny: PozycjaSlownikaReceptora[];
  zmiany: PozycjaSlownikaReceptora[];
  pobrano_at: string | null;
}

/**
 * Wynik `receptor.odczyt.nastawy` → słowniki. Czytany TOLERANCYJNIE: wpis bez
 * liczbowego `id` wypada, bo nie da się go zapisać w nastawach.
 */
export function slownikiZWyniku(wynik: unknown): SlownikiNastaw | null {
  if (typeof wynik !== 'object' || wynik === null) return null;
  const r = wynik as Record<string, unknown>;
  const lista = (w: unknown): PozycjaSlownikaReceptora[] =>
    Array.isArray(w)
      ? w
          .filter(
            (x): x is Record<string, unknown> =>
              typeof x === 'object' && x !== null && Number.isInteger((x as { id?: unknown }).id)
          )
          .map((x) => ({
            id: x.id as number,
            symbol: typeof x.symbol === 'string' ? x.symbol : null,
            nazwa: typeof x.nazwa === 'string' ? x.nazwa : null,
          }))
      : [];
  if (!Array.isArray(r.magazyny) || !Array.isArray(r.zmiany)) return null;
  return {
    magazyny: lista(r.magazyny),
    zmiany: lista(r.zmiany),
    pobrano_at: typeof r.pobrano_at === 'string' ? r.pobrano_at : null,
  };
}

/** Etykieta pozycji słownika: nazwa (symbol), a bez słownika sam numer. */
export function etykietaPozycji(
  id: number,
  slownik: PozycjaSlownikaReceptora[] | undefined
): string {
  const p = slownik?.find((x) => x.id === id);
  if (!p) return `nr ${id}`;
  const nazwa = p.nazwa ?? `nr ${id}`;
  return p.symbol ? `${nazwa} (${p.symbol})` : nazwa;
}

export interface OpisStatusuZadania {
  etykieta: string;
  wariant: 'default' | 'info' | 'success' | 'error';
  symbol: string;
}

export const OPIS_STATUSU_ZADANIA: Record<StatusZadania, OpisStatusuZadania> = {
  oczekuje: { etykieta: 'w kolejce', wariant: 'default', symbol: '◔' },
  podjete: { etykieta: 'wykonuje się', wariant: 'info', symbol: '●' },
  zrobione: { etykieta: 'zrobione', wariant: 'success', symbol: '✓' },
  blad: { etykieta: 'błąd', wariant: 'error', symbol: '✗' },
};

/** Czy zadanie jeszcze się nie skończyło — wtedy ekran odpytuje listę. */
export function zadanieWToku(z: ZadanieAgenta): boolean {
  return z.status === 'oczekuje' || z.status === 'podjete';
}

/**
 * Jedno zdanie o wyniku — dla człowieka, nie zrzut JSON-a.
 *
 * Kształt `wynik` jest kontraktem z `apps/receptor-agent/src/main/zadania.ts`:
 * ping niesie `wersja`/`host_nazwa`/`baza`, kartoteka `liczba`/`obciete`
 * i `wiersze[].id_receptury`. Czego streszczenie nie rozumie, tego nie
 * zgaduje — oddaje `null`, a ekran pokazuje sam status.
 */
export function streszczenieWyniku(z: ZadanieAgenta): string | null {
  if (z.status === 'blad') return z.blad;
  if (z.status !== 'zrobione') return null;
  const w = z.wynik;
  if (typeof w !== 'object' || w === null) return null;
  const r = w as Record<string, unknown>;

  if (z.rodzaj === 'receptor.ping') {
    const czesci = [
      typeof r.wersja === 'string' ? `agent ${r.wersja}` : null,
      typeof r.host_nazwa === 'string' ? r.host_nazwa : null,
      r.receptor_api === 'skonfigurowane' ? 'API Receptora: tak' : 'API Receptora: brak',
      r.baza === 'skonfigurowana' ? 'baza: tak' : 'baza: brak',
    ].filter((c): c is string => c !== null);
    return czesci.length > 0 ? czesci.join(' · ') : null;
  }

  if (z.rodzaj === 'receptor.odczyt.kartoteka') {
    if (typeof r.liczba !== 'number') return null;
    const wiersze = Array.isArray(r.wiersze) ? (r.wiersze as Array<Record<string, unknown>>) : [];
    const zReceptura = wiersze.filter(
      (x) => x.id_receptury !== null && x.id_receptury !== undefined
    ).length;
    const obciete = r.obciete === true ? ' (lista obcięta do sufitu)' : '';
    // Agent od wersji z obsługą Półproduktu podaje `typ` w każdym wierszu.
    // Starszy nie podaje — wtedy zdanie zostaje dawne, bez zgadywania podziału.
    const produktow = wiersze.filter((x) => x.typ === 'produkt').length;
    const polproduktow = wiersze.filter((x) => x.typ === 'polprodukt').length;
    const karty =
      produktow + polproduktow > 0
        ? `${r.liczba} kart: ${produktow} wariantów produktu i ${polproduktow} półproduktów`
        : `${r.liczba} wariantów produktu`;
    return `${karty}, ${zReceptura} z recepturą czynną${obciete}${zdaniePokryciaSku(r.pokrycie_sku)}`;
  }

  if (z.rodzaj === RODZAJ_ODCZYTU_NASTAW) {
    const s = slownikiZWyniku(w);
    return s ? `${s.magazyny.length} magazynów, ${s.zmiany.length} zmian` : null;
  }

  if (z.rodzaj === 'receptor.zlecenie.utworz') {
    if (typeof r.id_zlecenia_receptora !== 'number') return null;
    const pozycje = Array.isArray(r.pozycje) ? r.pozycje.length : null;
    // D2 (drugi przegląd 0.3.2): pozycja w jednostce innej niż karta NIE wchodzi
    // do zlecenia, reszta tak. Pominięcie ma być widoczne TU, nie tylko
    // w uwagach zlecenia w Receptorze — inaczej piekarnia upiecze część towaru,
    // a w Universe zobaczy zielone „utworzono”.
    const odrzucone = Array.isArray(r.pozycje_odrzucone)
      ? (r.pozycje_odrzucone as Array<Record<string, unknown>>)
      : [];
    const pominiete =
      odrzucone.length > 0
        ? `. POMINIĘTO ${odrzucone.length} poz. (jednostka niezgodna z kartą): ${odrzucone
            .map((p) => String(p.sku ?? '?'))
            .join(', ')}`
        : '';
    // UD-6c (`zlecenie-wynik/2`): numer `PO/…` z trasy RD-9a, potem numer wzorcowy
    // z bazy, a dopiero bez obu — identyfikator. Przy `juz_istnialo` bez dopisku
    // o potwierdzeniu: zlecenie mogło zostać potwierdzone od tamtej pory.
    const napis = (x: unknown): string | null =>
      typeof x === 'string' && x.trim().length > 0 ? x.trim() : null;
    const numer =
      napis(r.numer_zlecenia_receptora) ?? napis(r.numer_wzor) ?? `nr ${r.id_zlecenia_receptora}`;
    const juzIstnialo = r.juz_istnialo === true;
    return (
      `Zlecenie ${numer} w Receptorze` +
      (juzIstnialo ? ' (już istniało — nie utworzono drugiego)' : '') +
      (pozycje !== null
        ? `, ${pozycje} pozycji${juzIstnialo ? '' : ' — czeka na potwierdzenie w Managerze'}`
        : '') +
      pominiete
    );
  }

  if (z.rodzaj === RODZAJ_ODCZYTU_UZYTKOWNIKOW) {
    // Lista niesie STAN PIN-u, nie wartość — liczymy tylko tych, od których
    // PIN da się przenieść (dokładnie 4 cyfry).
    if (typeof r.liczba !== 'number') return null;
    const osoby = Array.isArray(r.uzytkownicy)
      ? (r.uzytkownicy as Array<Record<string, unknown>>)
      : [];
    const zgodnych = osoby.filter((o) => o?.stan_pinu === 'zgodny').length;
    const aktywne = r.tylko_aktywni === false ? '' : ' (aktywne)';
    const obciete = r.obciete === true ? ' (lista obcięta do sufitu)' : '';
    return `${r.liczba} osób z Receptora${aktywne}, ${zgodnych} z PIN-em do pobrania${obciete}`;
  }

  if (z.rodzaj === RODZAJ_POBRANIA_PINU) {
    // TYLKO status — pole `pin` nie jest czytane (patrz `statusPinuZWyniku`).
    const status = statusPinuZWyniku(w);
    if (status === null) return null;
    const osoba = Number.isInteger(r.id_user) ? ` (osoba nr ${r.id_user as number})` : '';
    return `${OPIS_STATUSU_PINU[status]}${osoba}`;
  }

  return null;
}

/**
 * Pokrycie kluczem SKU (agent 0.3.2, D7) — ile kart da się dopasować do SKU
 * z Mandatum, ZANIM pierwsze zlecenie wróci odmową. Starszy agent pola nie
 * podaje: zdanie zostaje dawne, bez zgadywania.
 */
function zdaniePokryciaSku(p: unknown): string {
  if (typeof p !== 'object' || p === null) return '';
  const q = p as Record<string, unknown>;
  const liczby = ['kart', 'z_symbolem', 'z_metadana', 'z_kluczem', 'klucz_mandatum'];
  if (!liczby.every((k) => typeof q[k] === 'number')) return '';
  let zdanie = `. Klucz SKU ma ${q.z_kluczem} z ${q.kart} kart (symbol: ${q.z_symbolem}, metadana MANDATUM: ${q.z_metadana})`;
  if (q.klucz_mandatum === 0) zdanie += ' — w bazie nie ma klucza metadanej MANDATUM';
  else if ((q.klucz_mandatum as number) > 1)
    zdanie += ` — klucz MANDATUM występuje ${q.klucz_mandatum} razy`;
  return zdanie;
}

// ── Wersja agenta i meldunek konfiguracji (0.3.2) ──────────────────────────

/** Najstarsza wersja agenta, która przyjmuje zlecenie bez zmiany (`WorkshiftIds: []`). */
export const WERSJA_ZLECEN_BEZ_ZMIANY = '0.3.2';

/**
 * Najstarsza wersja agenta, która czyta listę osób i PIN jednej osoby
 * (`WERSJA_PRACOWNIKOW_RECEPTORA` w receptor-core — usługa odmawia starszym
 * kodem `agent_za_stary`, panel po prostu nie proponuje takiego agenta).
 */
export const WERSJA_PRACOWNIKOW_RECEPTORA = '0.5.0';

/**
 * Czy wersja agenta jest co najmniej `minimalna`. `null` i napis nieparsowalny
 * dają `false` — wersji, której nie da się porównać, nie traktujemy jak nowej.
 * KOPIA funkcji z `services/receptor-core/src/zlecenie.ts` (powłoka nie zależy
 * od usług); zgodność pilnuje `tests/unit/apps/kontraktMeldunkuReceptora.test.ts`.
 */
export function wersjaAgentaCoNajmniej(
  wersja: string | null | undefined,
  minimalna: string
): boolean {
  const czesci = (w: string) => /^(\d+)\.(\d+)\.(\d+)/.exec(w.trim())?.slice(1).map(Number) ?? null;
  const a = typeof wersja === 'string' ? czesci(wersja) : null;
  const b = czesci(minimalna);
  if (!a || !b) return false;
  for (let i = 0; i < 3; i += 1) {
    if (a[i] !== b[i]) return a[i] > b[i];
  }
  return true;
}

// ── Aktualność odczytu magazynów i zmian (przegląd 0.3.2, T14) ─────────────

/** Pola wiersza agenta, od których zależy aktualność odczytu nastaw. */
export interface WierszKompletu {
  receptor_komplet_od?: string | null;
  receptor_nastawy_zdjete_at?: string | null;
}

/**
 * Chwila (ms), od której odczyt magazynów i zmian jest odczytem OBECNEJ bazy:
 * późniejsza z `receptor_komplet_od` (agent zgłosił obecne API i bazę)
 * i `receptor_nastawy_zdjete_at`. `null` = agent nie melduje kompletu
 * (wersja sprzed 0.3.2) — nie ma z czym porównać.
 * KOPIA `granicaOdczytuNastaw` z `services/receptor-core/src/index.ts` —
 * `PUT nastawy` stosuje tę samą regułę; zgodność pilnuje
 * `tests/unit/apps/kontraktMeldunkuReceptora.test.ts`.
 */
export function granicaOdczytuNastaw(agent: WierszKompletu): number | null {
  const chwile = [agent.receptor_komplet_od, agent.receptor_nastawy_zdjete_at]
    .filter((c): c is string => typeof c === 'string' && c.length > 0)
    .map((c) => Date.parse(c))
    .filter((c) => Number.isFinite(c));
  return chwile.length > 0 ? Math.max(...chwile) : null;
}

/**
 * Czy odczyt magazynów i zmian pochodzi z bazy, którą agent obsługuje TERAZ.
 * Do przeglądu 0.3.2 okno nastaw brało ostatni udany odczyt bez względu na
 * to, że agent zdążył zgłosić inną bazę — i podsuwało do zapisu identyfikatory
 * magazynów POPRZEDNIEJ bazy (T14). Porównujemy `created_at` zadania: odczyt
 * zlecony przed chwilą zgłoszenia obecnego kompletu jest nieaktualny, nawet
 * jeśli wykonał się później (wtedy jest zachowawczo „do ponowienia”).
 */
export function odczytNastawAktualny(
  odczyt: Pick<ZadanieAgenta, 'created_at'>,
  agent: WierszKompletu
): boolean {
  const granica = granicaOdczytuNastaw(agent);
  return granica === null || Date.parse(odczyt.created_at) >= granica;
}

/** Pola meldunku z wiersza agenta — podzbiór `AgentReceptora`, bez zależności od Supabase. */
export interface WierszMeldunku {
  app_version: string | null;
  registered_at: string | null;
  receptor_api_adres?: string | null;
  receptor_baza_serwer?: string | null;
  receptor_baza_instancja?: string | null;
  receptor_baza_port?: number | null;
  receptor_baza_nazwa?: string | null;
  receptor_baza_konto?: 'odczyt' | 'piaskownica' | null;
  receptor_konfiguracja_potwierdzona?: boolean | null;
  receptor_meldunek_at?: string | null;
  receptor_meldunek_odrzucony?: string | null;
}

export interface OpisMeldunku {
  stan: 'nie_podaje' | 'odrzucony' | 'brak_konfiguracji' | 'konfiguracja';
  tekst: string;
  /** Konfiguracja z pliku sprzed 0.3.2, niepotwierdzona przy serwerze — agent nie pracuje. */
  niepotwierdzona: boolean;
}

/**
 * Meldunek → jedno zdanie. Stany są NAZWANE i żaden nie udaje innego:
 * „nie podaje” (wersja sprzed 0.3.2 albo meldunek sprzed ponownego
 * parowania), „odrzucony” (agent 0.3.2 przysłał coś, czego usługa nie
 * przyjęła — to NIE jest „wersja sprzed 0.3.2”), „brak konfiguracji”
 * i konfiguracja z adresem API, bazą i trybem konta.
 */
export function opisMeldunku(a: WierszMeldunku): OpisMeldunku {
  const odrzucony = a.receptor_meldunek_odrzucony ?? null;
  const at = a.receptor_meldunek_at ?? null;
  const sprzedParowania =
    at !== null && a.registered_at !== null && Date.parse(at) < Date.parse(a.registered_at);
  if (odrzucony && (at === null || sprzedParowania)) {
    return {
      stan: 'odrzucony',
      tekst: `Agent przysłał meldunek o bazie i API, którego Universe nie przyjęło: ${odrzucony}`,
      niepotwierdzona: false,
    };
  }
  if (at === null || sprzedParowania) {
    return {
      stan: 'nie_podaje',
      tekst: wersjaAgentaCoNajmniej(a.app_version, '0.3.2')
        ? 'Agent jeszcze nie zameldował, którą bazę obsługuje — poczekaj na następny puls'
        : 'Agent nie podaje, którą bazę obsługuje (wersja sprzed 0.3.2)',
      niepotwierdzona: false,
    };
  }
  const api = a.receptor_api_adres ?? null;
  const serwer = a.receptor_baza_serwer ?? null;
  if (api === null && serwer === null) {
    return {
      stan: 'brak_konfiguracji',
      tekst: 'Agent nie ma konfiguracji Receptora',
      niepotwierdzona: false,
    };
  }
  const czesci: string[] = [];
  if (api) czesci.push(`API ${api}`);
  if (serwer) {
    const cel = a.receptor_baza_port
      ? `${serwer}:${a.receptor_baza_port}`
      : a.receptor_baza_instancja
        ? `${serwer}\\${a.receptor_baza_instancja}`
        : serwer;
    czesci.push(`baza ${cel}/${a.receptor_baza_nazwa ?? '?'}`);
    czesci.push(
      a.receptor_baza_konto === 'piaskownica'
        ? 'konto: piaskownica odczytu (login z prawem zapisu)'
        : a.receptor_baza_konto === 'odczyt'
          ? 'konto: tylko odczyt'
          : 'konto: nie sprawdzano'
    );
  }
  let tekst = czesci.join(' · ');
  if (odrzucony) tekst += ` — ostatni meldunek odrzucony: ${odrzucony}`;
  return {
    stan: 'konfiguracja',
    tekst,
    niepotwierdzona: a.receptor_konfiguracja_potwierdzona === false,
  };
}

// ── Wersja Receptora z pulsu agenta (0.5.2, migracja 760, plan 50 §3.1) ─────

/** Pierwsza wersja agenta, która melduje wersję Receptora. */
export const WERSJA_AGENTA_Z_WERSJA_RECEPTORA = '0.5.2';

/**
 * Odczyt starszy niż dwa okresy odpytywania agenta (15 min) dostaje na ekranie
 * swój wiek — puls niesie OSTATNI wynik, a po 5xx agent pyta co 60 min (K19).
 */
export const WIEK_WERSJI_DO_POKAZANIA_MS = 30 * 60_000;

/** Pola wersji z wiersza agenta — podzbiór `AgentReceptora`, bez zależności od Supabase. */
export interface WierszWersji {
  app_version: string | null;
  registered_at: string | null;
  receptor_wersja?: string | null;
  receptor_wersja_kod?: number | null;
  receptor_wersja_zrodlo?: 'api' | 'schemat' | 'nie_podaje' | null;
  receptor_wersja_slad?: Record<string, unknown> | null;
  receptor_wersja_at?: string | null;
}

export interface OpisWersjiReceptora {
  /**
   * CZTERY stany, nie trzy: `agent_nie_podaje` (NULL — agent sprzed 0.5.2) to
   * NIE jest `nie_podaje` (agent 0.5.2 umie, ale się nie dało — z powodem).
   */
  stan: 'agent_nie_podaje' | 'api' | 'schemat' | 'nie_podaje';
  tekst: string;
  /** Wymaga uwagi: produkcja w błędzie albo zablokowana, wersji nie odczytano. */
  uwaga: boolean;
  /** Szczegóły do podpowiedzi (commit, tryb modelu, indeks, dzienniki) — albo `null`. */
  szczegol: string | null;
}

/**
 * Zdania stanów produkcji (`OPIS_STANU_PRODUKCJI`) mieszkają w słowniku wspólnym;
 * tu zostaje decyzja ekranu panelu, które stany wymagają uwagi.
 */
const STANY_PRODUKCJI_Z_UWAGA = new Set(['Faulted', 'Blocked', 'blad']);

const obiekt = (x: unknown): Record<string, unknown> | null =>
  typeof x === 'object' && x !== null && !Array.isArray(x) ? (x as Record<string, unknown>) : null;
const tekstPola = (x: unknown): string | null => (typeof x === 'string' ? x : null);

/** Wpis dziennika na ekranie: `nie_podaje` i `blad` z Receptora to też nazwane stany. */
function wpisDziennika(x: unknown): string {
  const w = tekstPola(x);
  if (w === null || w === 'nie_podaje') return 'brak';
  if (w === 'blad') return 'błąd odczytu';
  return w;
}

/**
 * Wersja Receptora → jedno zdanie. Stany są NAZWANE i żaden nie udaje innego:
 * „Receptor 3.0.5.0”, „schemat: skrypt … / migracja …” (Receptor sprzed 3.0.5),
 * „wersji nie podaje (powód)” i — dla NULL — „agent nie podaje”.
 * Odczyt sprzed ponownego parowania należy do poprzedniego wpisu, więc dla
 * ekranu jest tym samym, co brak odczytu (jak meldunek konfiguracji).
 */
export function opisWersjiReceptora(a: WierszWersji, teraz: number): OpisWersjiReceptora {
  const zrodlo = a.receptor_wersja_zrodlo ?? null;
  const at = a.receptor_wersja_at ?? null;
  const sprzedParowania =
    at !== null && a.registered_at !== null && Date.parse(at) < Date.parse(a.registered_at);
  if (zrodlo === null || sprzedParowania) {
    return {
      stan: 'agent_nie_podaje',
      tekst: wersjaAgentaCoNajmniej(a.app_version, WERSJA_AGENTA_Z_WERSJA_RECEPTORA)
        ? 'Wersja Receptora: agent jeszcze jej nie podał — poczekaj na następny puls'
        : `Wersja Receptora: agent nie podaje (wersja agenta sprzed ${WERSJA_AGENTA_Z_WERSJA_RECEPTORA})`,
      uwaga: false,
      szczegol: null,
    };
  }
  const slad = obiekt(a.receptor_wersja_slad) ?? {};
  const wiek = at === null ? 0 : teraz - Date.parse(at);
  const dopisekWieku =
    wiek > WIEK_WERSJI_DO_POKAZANIA_MS ? ` · odczyt sprzed ${Math.floor(wiek / 60_000)} min` : '';

  if (zrodlo === 'nie_podaje') {
    const powod = tekstPola(slad.powod) ?? 'bez powodu';
    return {
      stan: 'nie_podaje',
      tekst: `Wersji Receptora nie podaje (${powod})${dopisekWieku}`,
      uwaga: true,
      szczegol: null,
    };
  }

  const glowna = obiekt(obiekt(slad.bazy)?.glowna) ?? {};
  const dziennikiGlownej = `skrypt ${wpisDziennika(glowna.database_change_log)} / migracja ${wpisDziennika(glowna.migracja_ef)}`;

  if (zrodlo === 'schemat') {
    return {
      stan: 'schemat',
      tekst: `Receptor sprzed 3.0.5 — schemat: ${dziennikiGlownej}${dopisekWieku}`,
      uwaga: false,
      szczegol:
        'Receptor nie ma adresu wersji (GET /api/Version) — agent odczytał ostatnie wpisy dzienników bazy głównej.',
    };
  }

  const produkcja = obiekt(slad.produkcja) ?? {};
  const stan = tekstPola(produkcja.stan) ?? 'nie_podaje';
  const blokada = tekstPola(produkcja.powod_blokady);
  const blad = tekstPola(produkcja.ostatni_blad);
  const opisProdukcji =
    (OPIS_STANU_PRODUKCJI[stan] ?? stan) +
    (stan === 'Blocked' && blokada ? ` (${blokada})` : '') +
    (stan === 'Faulted' && blad ? ` (${blad})` : '');
  const szczegol = [
    tekstPola(slad.commit) ? `commit ${tekstPola(slad.commit)}` : null,
    `tryb modelu: ${tekstPola(produkcja.tryb_modelu) ?? 'brak'}`,
    `indeks zleceń: ${tekstPola(produkcja.indeks) ?? 'brak'}`,
    `baza główna: ${dziennikiGlownej}`,
  ]
    .filter((x): x is string => x !== null)
    .join(' · ');
  return {
    stan: 'api',
    tekst: `Receptor ${a.receptor_wersja ?? '?'} · produkcja: ${opisProdukcji}${dopisekWieku}`,
    uwaga: STANY_PRODUKCJI_Z_UWAGA.has(stan),
    szczegol,
  };
}

// ── Synchronizacja dokumentów: zdolności agenta (UD-4, migracja 785) ────────

/** Pierwsza wersja agenta, która melduje gałąź `zdolnosci` (UD-4). */
export const WERSJA_AGENTA_ZE_ZDOLNOSCIAMI = '0.5.5';

/** Kontrakt synchronizacji dokumentów Receptora (RD-5a, Receptor 3.0.10.0+). */
export const KONTRAKT_DOKUMENTOW = 'dok-mag/1';

/*
 * Zdania powodów „nie sprawdzono” (`OPIS_POWODU_ZDOLNOSCI`, CHECK 785) mieszkają
 * w słowniku wspólnym — to samo zdanie widzi okno agenta w dziale Połączenia.
 */

/** Pola zdolności z wiersza agenta — podzbiór `AgentReceptora`, bez zależności od Supabase. */
export interface WierszZdolnosci {
  app_version: string | null;
  registered_at: string | null;
  zdolnosci?: string[] | null;
  receptor_zdolnosci?: string[] | null;
  receptor_zdolnosci_powod?: string | null;
  receptor_zdolnosci_at?: string | null;
  zdolnosci_at?: string | null;
}

export interface OpisSynchronizacjiDokumentow {
  /**
   * CZTERY stany: `agent_nie_podaje` (NULL — agent sprzed UD-4), `gotowy`
   * (zmierzone `dok-mag/1`), `brak_trasy` (zmierzone, lista pusta — Receptor
   * starszy) i `nie_sprawdzono` (z powodem). Zieleń WYŁĄCZNIE dla `gotowy`.
   */
  stan: 'agent_nie_podaje' | 'gotowy' | 'brak_trasy' | 'nie_sprawdzono';
  tekst: string;
  /** Zielone — wyłącznie zmierzona trasa. */
  dobrze: boolean;
  /** Wymaga działania człowieka (aktualizacja Receptora, token, API). */
  uwaga: boolean;
}

/**
 * Czy synchronizacja dokumentów ma tu czym rozmawiać — z sondy agenta.
 * Odczyt sprzed ponownego parowania należy do poprzedniego wpisu, więc dla
 * ekranu jest tym samym, co brak odczytu (jak wersja i meldunek).
 */
export function opisSynchronizacjiDokumentow(
  a: WierszZdolnosci,
  teraz: number
): OpisSynchronizacjiDokumentow {
  const at = a.zdolnosci_at ?? null;
  const sprzedParowania =
    at !== null && a.registered_at !== null && Date.parse(at) < Date.parse(a.registered_at);
  if (at === null || sprzedParowania) {
    return {
      stan: 'agent_nie_podaje',
      tekst: wersjaAgentaCoNajmniej(a.app_version, WERSJA_AGENTA_ZE_ZDOLNOSCIAMI)
        ? 'Synchronizacja dokumentów: agent jeszcze nie podał — poczekaj na następny puls'
        : `Synchronizacja dokumentów: agent nie podaje (wersja agenta sprzed ${WERSJA_AGENTA_ZE_ZDOLNOSCIAMI})`,
      dobrze: false,
      uwaga: false,
    };
  }
  const sondaAt = a.receptor_zdolnosci_at ?? null;
  const wiek = sondaAt === null ? 0 : teraz - Date.parse(sondaAt);
  const dopisekWieku =
    wiek > WIEK_WERSJI_DO_POKAZANIA_MS ? ` · sprawdzono ${Math.floor(wiek / 60_000)} min temu` : '';
  const kontrakty = a.receptor_zdolnosci ?? null;
  if (kontrakty === null) {
    const powod = a.receptor_zdolnosci_powod ?? '';
    return {
      stan: 'nie_sprawdzono',
      tekst: `Synchronizacja dokumentów: nie sprawdzono — ${OPIS_POWODU_ZDOLNOSCI[powod] ?? 'bez powodu'}${dopisekWieku}`,
      dobrze: false,
      uwaga: true,
    };
  }
  if (kontrakty.includes(KONTRAKT_DOKUMENTOW)) {
    return {
      stan: 'gotowy',
      tekst: `Synchronizacja dokumentów: Receptor gotowy (${KONTRAKT_DOKUMENTOW})${dopisekWieku}`,
      dobrze: true,
      uwaga: false,
    };
  }
  return {
    stan: 'brak_trasy',
    tekst: `Synchronizacja dokumentów: Receptor bez trasy dokumentów — wymaga wersji 3.0.10.0 lub nowszej${dopisekWieku}`,
    dobrze: false,
    uwaga: true,
  };
}
